import { useEffect, useMemo, useState } from 'react';
import { api, describeError, useOp } from '../api.ts';
import { useRouter } from '../router.tsx';
import type { DecideResult, ReviewItem, ReviewQueue, SitesTree } from '../types.ts';
import { announceChange, useChanges } from '../ui/changes.ts';
import { step, useKeys } from '../ui/keys.ts';
import { Empty, ErrorBox, Kbd, Loading, age, relativeDays } from '../ui/kit.tsx';
import { useToast } from '../ui/toasts.tsx';

const REASON_LABELS: Record<string, string> = {
  expired: 'Expired',
  not_working: 'Doesn’t work',
  duplicate: 'Duplicate',
  wrong_merchant: 'Wrong merchant',
  misleading_terms: 'Misleading terms',
  not_for_this_site: 'Not for this site',
  other: 'Other',
};

const keyOf = (item: Pick<ReviewItem, 'couponRef' | 'propertyRef'>) => `${item.couponRef}@${item.propertyRef}`;

export function Review() {
  const { query, setQuery } = useRouter();
  const toast = useToast();
  const changes = useChanges();
  const site = query.get('site') ?? '';
  const queue = useOp<ReviewQueue>('admin.review.queue', { propertyRef: site || undefined, limit: 200 }, changes);
  const sites = useOp<SitesTree>('admin.sites.list');
  const [decided, setDecided] = useState<Set<string>>(new Set());
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [cursor, setCursor] = useState(0);
  const [rejecting, setRejecting] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setDecided(new Set());
    setSelected(new Set());
  }, [queue.data]);

  const items = useMemo(() => (queue.data?.items ?? []).filter((item) => !decided.has(keyOf(item))), [queue.data, decided]);
  const current = items[Math.min(cursor, Math.max(0, items.length - 1))];
  const reasons = queue.data?.rejectReasons ?? Object.keys(REASON_LABELS);
  const siteOptions = sites.data?.organizations.flatMap((o) => o.brands.flatMap((b) => b.sites)) ?? [];

  const targets = (): ReviewItem[] => (selected.size > 0 ? items.filter((i) => selected.has(keyOf(i))) : current ? [current] : []);

  const decide = async (decision: 'approve' | 'reject', reason?: string) => {
    const batch = targets();
    if (batch.length === 0 || busy) return;
    setBusy(true);
    setRejecting(false);
    try {
      const result = await api.op<DecideResult>('admin.review.decide', {
        decision,
        reason,
        items: batch.map((i) => ({ couponRef: i.couponRef, propertyRef: i.propertyRef })),
      });
      const done = result.results.filter((r) => r.ok).map(keyOf);
      setDecided((set) => new Set([...set, ...done]));
      setSelected(new Set());
      const failed = result.results.filter((r) => !r.ok);
      const verb = decision === 'approve' ? 'Approved' : `Rejected (${REASON_LABELS[reason ?? ''] ?? reason})`;
      if (done.length) toast('success', `${verb}: ${done.length === 1 ? `${batch[0]!.merchant.name} on ${batch[0]!.propertyName}` : `${done.length} items`}`);
      if (failed.length) toast('error', `${failed.length} not decided: ${failed[0]!.error}`);
      if (selected.size === 0) setCursor((c) => Math.min(c, Math.max(0, items.length - 2)));
      announceChange();
    } catch (error) {
      toast('error', describeError(error));
    } finally {
      setBusy(false);
    }
  };

  const toggle = (item: ReviewItem | undefined) => {
    if (!item) return;
    setSelected((set) => {
      const next = new Set(set);
      const key = keyOf(item);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const reasonKeys = Object.fromEntries(reasons.map((reason, index) => [String(index + 1), () => rejecting && decide('reject', reason)]));
  useKeys({
    j: () => setCursor((c) => step(c, 1, items.length)),
    k: () => setCursor((c) => step(c, -1, items.length)),
    a: () => !rejecting && decide('approve'),
    'shift+a': () => !rejecting && selected.size > 0 && decide('approve'),
    r: () => targets().length > 0 && setRejecting(true),
    x: () => toggle(current),
    o: () => current && setQuery({ coupon: current.couponRef }),
    enter: () => current && setQuery({ coupon: current.couponRef }),
    escape: () => (rejecting ? setRejecting(false) : setSelected(new Set())),
    ...reasonKeys,
  }, !query.get('coupon'));

  return (
    <section className="page">
      <header className="page-head">
        <div>
          <h1>Review</h1>
          <p className="muted">
            {queue.data ? `${queue.data.total - decided.size} waiting` : ' '} · <Kbd>j</Kbd>/<Kbd>k</Kbd> move · <Kbd>a</Kbd> approve · <Kbd>r</Kbd> reject · <Kbd>x</Kbd> select · <Kbd>o</Kbd> open
          </p>
        </div>
        <label className="inline-field">
          Site
          <select value={site} onChange={(e) => setQuery({ site: e.target.value || null }, { replace: true })}>
            <option value="">All my sites</option>
            {siteOptions.map((s) => (
              <option key={s.ref} value={s.ref}>
                {s.name}
              </option>
            ))}
          </select>
        </label>
      </header>
      {queue.error ? <ErrorBox error={queue.error} onRetry={queue.reload} /> : null}
      {!queue.data && queue.loading ? <Loading /> : null}
      {queue.data && items.length === 0 ? <Empty>Queue is empty. New codes from sourcing, Slack and imports arrive here.</Empty> : null}
      {items.length > 0 ? (
        <table className="table review-table">
          <thead>
            <tr>
              <th scope="col" className="check">
                <input
                  type="checkbox"
                  aria-label="Select all"
                  checked={selected.size > 0 && selected.size === items.length}
                  onChange={(e) => setSelected(e.target.checked ? new Set(items.map(keyOf)) : new Set())}
                />
              </th>
              <th scope="col">Coupon</th>
              <th scope="col">Site</th>
              <th scope="col">Source</th>
              <th scope="col">Evidence</th>
              <th scope="col">Ends</th>
              <th scope="col" className="num">Waiting</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, index) => {
              const key = keyOf(item);
              const isCursor = item === current;
              return (
                <tr key={key} className={`${isCursor ? 'cursor' : ''} ${selected.has(key) ? 'selected' : ''}`} onClick={() => setCursor(index)} aria-selected={isCursor}>
                  <td className="check">
                    <input type="checkbox" aria-label={`Select ${item.merchant.name}: ${item.title}`} checked={selected.has(key)} onChange={() => toggle(item)} onClick={(e) => e.stopPropagation()} />
                  </td>
                  <td>
                    <span className="strong">{item.merchant.name}</span> · {item.title}
                    <small className="block muted">
                      {item.code ? <code>{item.code}</code> : 'deal'} · {item.discount}
                      {item.conditions ? ` · ${item.conditions.split('\n')[0]}` : ''}
                    </small>
                  </td>
                  <td>
                    {item.propertyName}
                    {!item.merchant.shownOnSite ? (
                      <span className="badge badge-warn" title="The merchant is not shown on this site yet; approving keeps the coupon off the page until it is">
                        merchant hidden
                      </span>
                    ) : null}
                  </td>
                  <td>
                    {item.aiDraft ? <span className="badge badge-ai" title="Drafted by AI: read the title and terms closely">AI draft</span> : null} {item.source.type.replace(/_/g, ' ')}
                    {item.source.confidence !== null ? <small className="block muted">{Math.round(item.source.confidence * 100)}% confidence</small> : null}
                    {item.source.url ? (
                      <small className="block">
                        <a href={item.source.url} target="_blank" rel="noreferrer noopener" onClick={(e) => e.stopPropagation()}>
                          where it was found
                        </a>
                      </small>
                    ) : null}
                  </td>
                  <td>
                    {item.evidence.lastCheck ? (
                      <span className={item.evidence.lastCheck.result === 'worked' ? 'text-ok' : 'text-warn'}>
                        {item.evidence.lastCheck.result} · {item.evidence.lastCheck.method}
                      </span>
                    ) : (
                      <span className="muted">not checked</span>
                    )}
                    {item.evidence.openFlags.map((f) => (
                      <small key={f.flag} className="block text-warn">
                        {f.flag}: {f.message}
                      </small>
                    ))}
                  </td>
                  <td>{relativeDays(item.endsAt)}</td>
                  <td className="num muted">{age(item.queuedAt)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      ) : null}
      {items.length > 0 ? (
        <div className={`decision-bar ${rejecting ? 'rejecting' : ''}`} role="region" aria-label="Decision">
          {rejecting ? (
            <>
              <span>
                Reject {targets().length > 1 ? `${targets().length} items` : 'this item'} because…
              </span>
              <div className="chips">
                {reasons.map((reason, index) => (
                  <button key={reason} type="button" className="chip" onClick={() => decide('reject', reason)}>
                    <Kbd>{index + 1}</Kbd> {REASON_LABELS[reason] ?? reason}
                  </button>
                ))}
              </div>
              <button type="button" className="btn btn-ghost btn-small" onClick={() => setRejecting(false)}>
                Cancel <Kbd>Esc</Kbd>
              </button>
            </>
          ) : (
            <>
              <span>{selected.size > 0 ? `${selected.size} selected` : current ? `${current.merchant.name} on ${current.propertyName}` : ''}</span>
              <button type="button" className="btn btn-ok" disabled={busy} onClick={() => decide('approve')}>
                {selected.size > 0 ? `Approve ${selected.size}` : 'Approve'} <Kbd>{selected.size > 0 ? '⇧A' : 'A'}</Kbd>
              </button>
              <button type="button" className="btn" disabled={busy} onClick={() => setRejecting(true)}>
                Reject… <Kbd>R</Kbd>
              </button>
              {current ? (
                <button type="button" className="btn btn-ghost" onClick={() => setQuery({ coupon: current.couponRef })}>
                  Open <Kbd>O</Kbd>
                </button>
              ) : null}
            </>
          )}
        </div>
      ) : null}
    </section>
  );
}
